import { useMemo } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { OverlayScrollArea } from '@odo/shared-ui';
import type { ConversationQuickReply } from '../../../services/conversationQuickReplies';
import { quickReplyDocText } from '../chat/quickReplyDocText';
import { filterQuickReplies } from '../filterQuickReplies';
import { groupByCategory, type QuickReplyCategoryGroup } from './groupByCategory';
import { QuickReplyAdminRow } from './QuickReplyAdminRow';

interface QuickRepliesManageListProps {
    replies: ConversationQuickReply[];
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
    query: string;
    onQueryChange: (query: string) => void;
    onNew: () => void;
    onEdit: (reply: ConversationQuickReply) => void;
    onDelete: (reply: ConversationQuickReply) => void;
}

function visibleGroups(
    replies: ConversationQuickReply[],
    query: string
): QuickReplyCategoryGroup[] {
    const candidates = replies.map((reply) => ({
        ...reply,
        key: reply.id,
        content: quickReplyDocText(reply.bodyDoc)
    }));
    return groupByCategory(filterQuickReplies(candidates, query));
}

export function QuickRepliesManageList({
    replies,
    isLoading,
    isError,
    onRetry,
    query,
    onQueryChange,
    onNew,
    onEdit,
    onDelete
}: QuickRepliesManageListProps) {
    const groups = useMemo(() => visibleGroups(replies, query), [replies, query]);

    return (
        <>
            <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200">
                <div className="flex items-center flex-1 min-w-0 h-9 bg-white border border-gray-200 rounded-xl px-3">
                    <MagnifyingGlassIcon className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => onQueryChange(e.target.value)}
                        placeholder="Search quick replies"
                        aria-label="Search quick replies"
                        className="flex-1 min-w-0 bg-transparent outline-none text-sm text-gray-700 placeholder-gray-400"
                    />
                </div>
                <button
                    type="button"
                    onClick={onNew}
                    className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors flex-shrink-0"
                >
                    + New quick reply
                </button>
            </div>
            <OverlayScrollArea
                className="flex-1 min-h-0 bg-gray-50"
                contentClassName="px-6 py-5"
                trackInsetPx={4}
            >
                <QuickRepliesManageBody
                    isLoading={isLoading}
                    isError={isError}
                    onRetry={onRetry}
                    groups={groups}
                    onEdit={onEdit}
                    onDelete={onDelete}
                />
            </OverlayScrollArea>
        </>
    );
}

interface QuickRepliesManageBodyProps {
    isLoading: boolean;
    isError: boolean;
    onRetry: () => void;
    groups: QuickReplyCategoryGroup[];
    onEdit: (reply: ConversationQuickReply) => void;
    onDelete: (reply: ConversationQuickReply) => void;
}

function QuickRepliesManageBody({
    isLoading,
    isError,
    onRetry,
    groups,
    onEdit,
    onDelete
}: QuickRepliesManageBodyProps) {
    if (isLoading) return <p className="text-sm text-gray-500">Loading quick replies…</p>;
    if (isError) {
        return (
            <div className="flex items-center gap-3" role="alert">
                <p className="text-sm text-red-500">Could not load quick replies.</p>
                <button
                    type="button"
                    onClick={onRetry}
                    className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                >
                    Retry
                </button>
            </div>
        );
    }
    if (groups.length === 0) {
        return <p className="text-sm text-gray-500">No quick replies found.</p>;
    }

    return (
        <div className="flex flex-col gap-6">
            {groups.map((group) => (
                <section key={group.category} aria-label={group.category}>
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                        {group.category}
                    </h3>
                    <ul className="bg-white rounded-xl border border-gray-200 divide-y divide-gray-200 overflow-hidden">
                        {group.replies.map((reply) => (
                            <QuickReplyAdminRow
                                key={reply.id}
                                reply={reply}
                                onEdit={onEdit}
                                onDelete={onDelete}
                            />
                        ))}
                    </ul>
                </section>
            ))}
        </div>
    );
}
